// Common API response types
export interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
  statistics_data?: {
    status_counts?: Record<string, number>
    [key: string]: unknown
  }
}

export interface ApiError {
  detail?: string
  message?: string
  error?: string
  [key: string]: unknown
}

export interface ApiResponse<T = unknown> {
  status?: string
  message?: string
  more_info?: Record<string, unknown>
  data: T
}

export interface JobSummary {
  total_expenses: number
  work_days_count: number
  total_hours: number
  total_workers: number
  internal_workers: number
  contract_workers: number
}

export interface JobPayrollSummary {
  total_payroll: number | string
  workers?: Array<{
    worker_id?: string | number
    worker_name?: string
    hours?: number | string
    pay?: number | string
  }>
}

export interface JobOrderInfo {
  id?: string | number
  reference_id: string
  status?: string | number | null
  type?: string | number | null
  type_label?: string | null
  is_paid?: boolean | null
  payment_method?: string | number | null
  created_on?: string | null
  customer_name?: string | null
  email?: string | null
  phone?: string | null
  address?: string | null
}

export interface PlannerParameterDefinition {
  id: string | number
  key: string
  label: string
  data_type: string
  unit?: string | null
  required?: boolean
  ordering?: number
  default_value_json?: unknown
  choices_json?: unknown[]
  rules_json?: Record<string, unknown> | null
}

export interface PlannerResourceDefinition {
  id: string | number
  planner_model?: string | number | null
  role_name?: string | number | null
  crew_template?: string | number | null
  crew_template_name?: string | null
  crew_template_code?: string | null
  crew_template_logo_url?: string | null
  role_name_unique_id?: string | null
  role_name_title?: string | null
  role_name_is_system?: boolean | null
  role_code: string
  label: string
  resource_kind: string
  is_primary?: boolean
  min_count?: number | null
  max_count?: number | null
  preferred_count?: number | null
  ordering?: number
  dependency_rules_json?: Record<string, unknown> | null
  capability_schema_json?: Record<string, unknown> | null
}

export interface PlannerWorkerCandidate {
  id: string | number
  display_name?: string | null
  phone?: string | null
  email?: string | null
  role?: string | number | null
  role_name?: string | null
  service_instance?: string | number | null
  post?: string | number | null
  is_active?: boolean | null
}

export interface PlannerModel {
  id: string | number
  code: string
  name: string
  slug?: string | null
  description?: string | null
  owner_service_instance?: string | number | null
  job_category?: string | number | null
  template_code?: string | null
  model_version?: number | null
  contract_version?: string | null
  planning_mode?: string | null
  job_scope?: string | null
  job_trigger_status?: string | number | null
  job_creation_mode?: string | null
  priority_mode_enabled?: boolean | null
  priority_job_trigger_status?: string | number | null
  priority_job_creation_mode?: string | null
  strategy?: string | null
  fallback_strategy?: unknown
  status?: string | null
  is_system?: boolean
  active?: boolean
  deleted?: boolean
  published_at?: string | null
  config_json?: Record<string, unknown> | null
  parameters?: PlannerParameterDefinition[]
  resource_definitions?: PlannerResourceDefinition[]
  connected_catalog_count?: number
}

export interface PlannerResourceLink {
  id: string | number
  service_instance?: string | number | null
  service_instance_name?: string | null
  service_instance_identifier?: string | null
  planner_model?: string | number | null
  planner_model_id?: string | number | null
  planner_model_code?: string | null
  planner_model_name?: string | null
  planner_model_template_code?: string | null
  planner_resource?: string | number | null
  planner_resource_id?: string | number | null
  planner_resource_role_name_id?: string | number | null
  planner_resource_role_name_unique_id?: string | null
  planner_resource_role_name_title?: string | null
  planner_resource_role_code?: string | null
  planner_resource_label?: string | null
  planner_resource_kind?: string | null
  planner_resource_crew_template_id?: string | number | null
  planner_resource_crew_template_name?: string | null
  default_crew_id?: string | number | null
  default_crew_name?: string | null
  planner_resource_min_count?: number | null
  planner_resource_preferred_count?: number | null
  planner_resource_max_count?: number | null
  product?: string | number | null
  product_name?: string | null
  product_variant?: string | number | null
  product_variant_label?: string | null
  product_variant_product_name?: string | null
  addon_item?: string | number | null
  addon_item_name?: string | null
  addon_group_name?: string | null
  order?: string | number | null
  order_reference_id?: string | null
  order_type?: string | number | null
  order_type_key?: string | null
  order_type_label?: string | null
  serve_type?: string | number | null
  serve_type_key?: string | null
  serve_type_label?: string | null
  job_trigger_status?: string | number | null
  job_trigger_status_label?: string | null
  job_creation_mode?: string | null
  apply_to_all_orders?: boolean | null
  quantity?: number | null
  scope?: string | null
  is_required?: boolean | null
  notes?: string | null
  config_json?: Record<string, unknown> | null
  target_type?: 'product' | 'product_variant' | 'addon_item' | 'order' | 'global_order' | null
  target_label?: string | null
  effective_assignment_slots?: number | null
  created_on?: string | null
  updated_on?: string | null
}

export interface CrewTemplateSlot {
  id?: string | number
  crew_template?: string | number
  skill_role: string | number
  skill_role_name?: string | null
  skill_role_unique_id?: string | null
  quantity?: number
  is_primary?: boolean
  slot_label?: string
  ordering?: number
}

export interface CrewTemplate {
  id: string | number
  service_instance?: string | number
  name: string
  code: string
  slug?: string
  description?: string
  logo_url?: string
  job_category?: string | number | null
  job_category_name?: string | null
  active?: boolean
  slots?: CrewTemplateSlot[]
  slot_count?: number
  required_headcount?: number
}

export interface CrewMember {
  id: string | number
  crew: string | number
  crew_template_slot: string | number
  worker_skill: string | number
  worker_display_name?: string | null
  skill_role_id?: string | number | null
  skill_role_name?: string | null
  slot_label?: string | null
  ordering?: number
}

export interface Crew {
  id: string | number
  crew_template: string | number
  crew_template_name?: string | null
  crew_template_logo_url?: string | null
  service_instance?: string | number
  name: string
  code: string
  slug?: string
  active?: boolean
  members?: CrewMember[]
  is_ready?: boolean
  required_headcount?: number
  member_count?: number
}

export interface StoreOrderSummary {
  id: string
  reference_id: string
  status?: string | number | null
  status_label?: string | null
  is_terminal?: boolean | null
  notes?: string | null
  type?: string | number | null
  type_label?: string | null
  order_type?: string | number | null
  order_type_key?: string | null
  order_type_label?: string | null
  serve_type?: string | number | null
  serve_type_key?: string | null
  serve_type_label?: string | null
  source_type?: string | number | null
  source_type_str?: string | null
  order_time?: string | null
  delivery_time?: string | null
  created_on?: string | null
  is_scheduled?: boolean | null
  is_paid?: boolean | null
  payment_method?: string | number | null
  payment_status?: string | number | null
  payment_url?: string | null
  total?: string | number | null
  currency?: string | null
  customer_name?: string | null
  customer_id?: string | number | null
  customer_email?: string | null
  customer_phone?: string | null
  address_label?: string | null
  order_item_count?: number
  linked_job_count?: number
  job_id?: string | number | null
  job_ref?: string | null
  service_instance_id?: string | null
  job_service_instance_id?: string | null
  jobs_summary?: OrderJobSummary[]
  recurrence?: OrderRecurrenceInfo | null
}

export interface OrderRecurrenceInfo {
  rule: string
  interval_days?: number | null
  weekdays?: number[]
  month_day?: number | null
  end_date?: string | null
  occurrence_count?: number | null
  payment_plan?: string | null
  per_occurrence_total?: string | null
  is_bounded?: boolean
}

export interface StoreOrderItem {
  id?: string | number | null
  _id?: string | number | null
  included_features?: Array<{
    id?: string | number | null
    name?: string | null
    code?: string | null
    feature_type?: string | null
  }>
  excluded_features?: Array<{
    id?: string | number | null
    name?: string | null
    code?: string | null
    feature_type?: string | null
  }>
  product_name?: string | null
  product_type?: string | number | null
  product_variant_name?: string | null
  addon_names?: string[]
  priority_booking_selected?: boolean | number | string | null
  priority_fee?: string | number | null
  booking_fee?: string | number | null
  planner_resource_links?: PlannerResourceLink[]
  qty?: number | null
  unit_price?: string | number | null
  final_price?: string | number | null
  location_text?: string | null
  latitude?: string | number | null
  longitude?: string | number | null
  offer_title?: string | null
  discount_value?: string | number | null
  discount_type?: string | number | null
  applied_offer?: string | number | null
  applied_offer_data?: Record<string, unknown> | null
  job_id?: string | number | null
  job_ref?: string | null
  job_status?: string | null
  job_service_instance_id?: string | null
  has_job?: boolean | null
  assigned_worker_count?: number | null
}

export interface StoreOrderItemMedia {
  id: string
  _id?: string | number | null
  order_item?: string | null
  _order_item?: string | number | null
  basket_item?: string | null
  _basket_item?: string | number | null
  mime?: string | null
  path?: string | null
  file_size?: number | null
  type?: string | number | null
  type_str?: string | null
  created_on?: string | null
  updated_on?: string | null
}

export interface StoreOrderTotalLine {
  label: string
  amount?: string | number | null
  currency?: string | null
  sign?: string | null
  value?: string | number | null
}

export interface OrderJobSummary {
  id: string | number
  job_ref?: string | null
  title?: string | null
  status?: string | null
  order_item_id?: string | number | null
  scheduled_start?: string | null
  assigned_worker_count?: number
  service_instance_id?: string | null
  service_instance_pk?: string | number | null
}

export interface StoreOrderDetail extends StoreOrderSummary {
  _id?: string | number | null
  raw_id?: number | null
  service_instance?: string | number | null
  service_instance_id?: string | null
  service_instance_name?: string | null
  full_name?: string | null
  email?: string | null
  mobile?: string | null
  payment_status?: string | number | null
  payment_mode_name?: string | null
  payment_url?: string | null
  final_total?: string | number | null
  planner_resource_links?: PlannerResourceLink[]
  order_items: StoreOrderItem[]
  totals: StoreOrderTotalLine[]
  status_history?: Array<Record<string, unknown>>
  payment_data?: Array<Record<string, unknown>>
  offers_applied?: Array<Record<string, unknown>>
  delivery_address_data?: Record<string, unknown> | null
  user_data?: Record<string, unknown> | null
  /** POS / POS_DEVICE sold-by staff (from checkout meta or created_by user). */
  staff_name?: string | null
  staff_reg?: string | null
  created_by_data?: {
    id?: string | number | null
    name?: string | null
    usr_reg_num?: string | null
  } | null
  job_id?: string | number | null
  has_job?: boolean
  job_scope?: string | null
  job_ids?: Array<string | number>
  job_service_instance_id?: string | null
  jobs_summary?: OrderJobSummary[]
}

export interface StoreOrderPayment {
  id: string | number
  _id?: string | number | null
  amount?: string | number | null
  currency?: string | null
  payment_reference_id?: string | null
  payment_reference_number?: string | null
  order_reference_id?: string | null
  customer_name?: string | null
  customer_phone?: string | null
  customer_email?: string | null
  payment_service?: string | number | null
  payment_method?: string | number | null
  payment_mode?: string | number | null
  payment_status?: string | number | null
  created_on?: string | null
  updated_on?: string | null
}

export interface StoreOrderRefund {
  id: string | number
  _id?: string | number | null
  order?: string | number | null
  order_id?: string | number | null
  order_reference_id?: string | number | null
  order_total_amount?: string | number | null
  payment_transaction?: string | number | null
  amount?: string | number | null
  gross_amount?: string | number | null
  deduction_amount?: string | number | null
  currency?: string | null
  status?: string | number | null
  source?: string | null
  settlement_type?: string | null
  refund_wallet_id?: string | number | null
  refund_wallet_label?: string | null
  applied_rule_id?: string | number | null
  refund_rule_title?: string | null
  gateway_refund_id?: string | null
  reason?: string | null
  rule_snapshot?: Record<string, unknown> | null
  payment_method_label?: string | null
  payment_reference_id?: string | null
  gateway_payment_reference_id?: string | null
  paid_status?: string | null
  paid_on?: string | null
  auto_refund_eligible?: boolean | null
  auto_refund_reason?: string | null
  initiated_by?: {
    id?: string | number | null
    name?: string | null
  } | null
  reviewed_by?: {
    id?: string | number | null
    name?: string | null
  } | null
  created_on?: string | null
  updated_on?: string | null
}

export interface StoreOrderRefundRule {
  id?: string | number | null
  trigger_type: string
  label?: string | null
  min_hours_before?: string | number | null
  max_hours_before?: string | number | null
  deduction_type: 'percentage' | 'flat'
  deduction_value: string | number
  refund_basis: 'paid_amount' | 'order_total' | 'booking_fee'
  priority?: number
  active?: boolean
}

export interface StoreOrderRefundPolicy {
  id: string | number
  service_instance_id?: string | number | null
  order_type_id?: string | number | null
  order_type_key?: string | null
  order_type_label?: string | null
  reference_event: 'service_time' | 'order_time'
  default_no_match: 'no_refund' | 'full_refund'
  default_refund_wallet_id?: string | number | null
  default_refund_wallet_label?: string | null
  active?: boolean
  rules: StoreOrderRefundRule[]
}

export interface StoreOrderRefundPreview {
  policy_id?: string | number | null
  rule_id?: string | number | null
  trigger_type?: string | null
  reference_event?: string | null
  hours_before_reference?: string | number | null
  gross_amount?: string | number | null
  deduction_amount?: string | number | null
  refund_amount?: string | number | null
  remaining_refundable_amount?: string | number | null
  paid_cap?: string | number | null
  settlement_type?: string | null
  settlement_message?: string | null
  requires_manual_action?: boolean
  refund_wallet_id?: string | number | null
  refund_wallet_label?: string | null
  rule_snapshot?: Record<string, unknown> | null
  has_policy?: boolean
}

export interface StoreCategory {
  id: string | number
  _id?: string | number | null
  name: string
  logo?: string | null
  icon?: string | null
  image?: string | null
  thumbnail?: string | null
  category_logo?: string | null
  slug?: string | null
  short_code?: string | null
  description?: string | null
  parent?: string | number | null
  parent_id?: string | number | null
  _parent_id?: string | number | null
  parent_name?: string | null
  master_category_id?: string | number | null
  product_count?: number
  category_type?: 'normal' | 'membership_plan' | string
  active?: number | boolean | null
  is_active?: boolean | number | null
  created_at?: string | null
  updated_at?: string | null
}

export interface StoreCategoryPayload {
  name: string
  slug?: string
  short_code?: string
  description?: string
  parent?: string | number | null
  category_type?: 'normal' | 'membership_plan' | string
  is_active?: boolean
}

export const STORE_TAG_ITEM_TYPES = {
  all: 0,
  product: 1,
  category: 2,
  productVariant: 3,
  offer: 4,
  store: 5,
} as const

export type StoreTagItemType = typeof STORE_TAG_ITEM_TYPES[keyof typeof STORE_TAG_ITEM_TYPES]

export type StoreTagAssignmentTarget = 'product' | 'product_variant' | 'category' | 'offer' | 'store'

export interface StoreTagset {
  id: string | number
  _id?: string | number | null
  name: string
  code: string
  logo?: string | null
  icon?: string | null
  sample_view?: string | null
  max_tags?: string | number | null
  item_types?: StoreTagItemType | number | null
  active?: number | boolean
  tag_data?: unknown[]
  created_on?: string | null
  updated_on?: string | null
  scope?: 'global' | 'organization' | 'local'
  service_variant?: string
}

export interface StoreTagsetPayload {
  name: string
  code: string
  max_tags: string | number
  item_types: StoreTagItemType | number
  active?: number | boolean
  org_instance?: string | number
  logo?: File | string | null
  icon?: File | string | null
  sample_view?: File | string | null
  scope: 'global' | 'organization' | 'local'
  service_variant?: string
}

export interface StoreTag {
  id: string | number
  _id?: string | number | null
  tagset?: string | number | null
  _tagset?: string | number | null
  tagset_name?: string | null
  title: string
  code: string
  item_types?: StoreTagItemType | number | null
  logo?: string | null
  icon?: string | null
  sample_view?: string | null
  max_item?: string | number | null
  max_items?: string | number | null
  sorting_order?: string | number | null
  active?: number | boolean
  scope?: 'global' | 'organization' | 'local'
  created_on?: string | null
  updated_on?: string | null
}

export interface StoreTagPayload {
  tagset?: string | number
  title: string
  code?: string
  max_item?: string | number
  max_items?: string | number
  sorting_order?: string | number
  item_types?: StoreTagItemType | number
  active?: number | boolean
  org_instance?: string | number
  logo?: File | string | null
  icon?: File | string | null
  sample_view?: File | string | null
}

export interface StoreTaggedItem {
  id: string | number
  tag?: string | number | null
  tag_id?: string | number | null
  tag__id?: string | number | null
  tagset_id?: string | number | null
  tagset_name?: string | null
  title?: string | null
  code?: string | null
  logo?: string | null
  icon?: string | null
  sample_view?: string | null
  item_type?: StoreTagItemType | number | null
  product?: string | number | null
  _product?: string | number | null
  product_variant?: string | number | null
  _product_variant?: string | number | null
  category?: string | number | null
  _category?: string | number | null
  offer?: string | number | null
  _offer?: string | number | null
  node?: string | number | null
  _node?: string | number | null
  active?: number | boolean
  sorting_order?: string | number | null
}

export interface StoreTaggedItemPayload {
  tag: string | number
  product?: string | number
  product_variant?: string | number
  category?: string | number
  offer?: string | number
  node?: string | number
  sorting_order?: string | number
  org_instance?: string | number
}

// ---------------------------------------------------------------------------
// Product Feature Catalog
// ---------------------------------------------------------------------------

export type ProductFeatureScopeType = 'global' | 'org' | 'instance'
export type ProductFeatureType = 'text' | 'boolean' | 'icon' | 'image' | 'badge'

export interface StoreProductFeature {
  id: string | number
  _id?: string | number | null
  code: string
  name: string
  description?: string | null
  feature_type: ProductFeatureType
  display_text?: string | null
  icon?: string | null
  image?: string | null
  sort_order?: number
  scope_type: ProductFeatureScopeType
  is_system: boolean
  active?: number | boolean
}

export interface StoreProductFeaturePayload {
  name: string
  code: string
  scope_type: ProductFeatureScopeType
  description?: string
  feature_type?: ProductFeatureType
  display_text?: string
  icon?: string
  image?: string
  sort_order?: number
}

export interface StoreProductFeatureAssignment {
  id: string | number
  feature: StoreProductFeature
  inclusive: boolean
}

export interface StoreProductFeatureAssignmentSet {
  included: (string | number)[]
  excluded: (string | number)[]
}

export interface StoreProductFeatureAssignmentsResponse {
  included: StoreProductFeatureAssignment[]
  excluded: StoreProductFeatureAssignment[]
}

export interface StoreVariantFeatureAssignmentsResponse {
  product_included?: StoreProductFeatureAssignment[]
  product_excluded?: StoreProductFeatureAssignment[]
  variant_included: StoreProductFeatureAssignment[]
  variant_excluded: StoreProductFeatureAssignment[]
  effective_included: StoreProductFeatureAssignment[]
  effective_excluded: StoreProductFeatureAssignment[]
}

export interface CountryTax {
  id: string | number
  name: string
  code: string
  rate: string | number
  active?: boolean
  sort_order?: number
}

export interface CountryTaxBundle {
  id: string | number
  name: string
  code: string
  description?: string | null
  total_percentage?: string | number
  country_id?: string | number
  country_name?: string | null
  tax_system?: string | number
  tax_system_name?: string | null
  active?: boolean
  taxes?: CountryTax[]
}

export interface CountryTaxSystem {
  id: string | number
  name: string
  code: string
  country: string | number
  country_name?: string | null
  active?: boolean
  bundles?: CountryTaxBundle[]
}

export interface TaxLabelData {
  id?: string | number | null
  _id?: string | number | null
  name?: string | null
  description?: string | null
  total_percentage?: string | number | null
  country_tax_bundle?: string | number | null
  country_tax_bundle_name?: string | null
  country_tax_bundle_code?: string | null
}

export interface TaxLabel {
  id: string | number
  _id?: string | number | null
  name: string
  description?: string | null
  active?: boolean | number | string
  country_tax_bundle?: string | number | null
  country_tax_bundle_data?: CountryTaxBundle | null
  total_percentage?: string | number | null
}

export interface StoreProduct {
  id: string | number
  _id?: string | number | null
  raw_id?: number | null
  name: string
  logo?: string | null
  slug?: string | null
  short_code?: string | null
  description?: string | null
  category?: string | number | null
  category_name?: string | null
  _category_id?: string | number | null
  master_id?: string | number | null
  sku?: string | null
  base_price?: string | number | null
  active?: boolean | number | string | null
  is_active?: boolean | number | string | null
  product_type?: string | number | null
  brand?: string | number | null
  brand_name?: string | null
  product_unique_name?: string | number | null
  product_unique_name_label?: string | null
  variant_count?: number
  planner_resource_links?: PlannerResourceLink[]
  tax_label?: string | number | null
  tax_label_data?: TaxLabelData | null
  created_at?: string | null
  updated_at?: string | null
}

export interface StoreProductPayload {
  name: string
  slug?: string
  short_code?: string
  description?: string
  category?: string | number | null
  master_product_id?: string | number | null
  brand?: string | number | null
  product_unique_name?: string | number | null
  sku?: string
  base_price?: string | number
  active?: boolean | number | string
  is_active?: boolean
  product_type?: string | number | null
  deactivate_variants?: boolean | number | string
  tax_label?: string | number | null
}

export interface StoreProductUniqueName {
  id: string | number
  name: string
  active?: number | boolean
  is_active?: boolean
  logo?: string | null
  icon?: string | null
}

export interface StoreProductUniqueNamePayload {
  name: string
  active?: number
}

export interface StoreBrand {
  id: string | number
  name: string
  active?: number | boolean
  is_active?: boolean
  logo?: string | null
  icon?: string | null
}

export interface StoreBrandPayload {
  name: string
  active?: number
}

export interface StoreProductVariant {
  id: string | number
  _id?: string | number | null
  product?: string | number | null
  product_name?: string | null
  product_active?: boolean | number | string | null
  category?: string | number | null
  category_name?: string | null
  _category_id?: string | number | null
  planner_model?: string | number | null
  planner_model_id?: string | number | null
  planner_model_code?: string | null
  planner_model_name?: string | null
  planner_model_template_code?: string | null
  logo?: string | null
  label?: string | null
  name?: string | null
  description?: string | null
  sku?: string | null
  stock_keeping_unit?: string | null
  price?: string | number | null
  quantity?: string | number | null
  mrp_price?: string | number | null
  sell_price?: string | number | null
  buy_price?: string | number | null
  priority_booking_enabled?: boolean | number | string | null
  priority_booking_default?: boolean | number | string | null
  priority_fee?: string | number | null
  priority_service_time_mins?: string | number | null
  booking_fee?: string | number | null
  taxes_included?: boolean | number | string | null
  tax_label?: string | number | null
  tax_label_data?: TaxLabelData | null
  price_data?: StoreProductVariantPrice | null
  banner_data?: StoreProductVariantMedia[]
  gallery_data?: StoreProductVariantMedia[]
  planner_resource_links?: PlannerResourceLink[]
  processing_time?: string | number | null
  processing_unit?: 'min' | 'hr' | 'day' | null
  approx_processing_time?: string | number | null
  approx_processing_unit?: 'hr' | 'day' | null
  /** Joined proportions; used by POS for sell-unit (kg/L/m) qty prompting. */
  proportions?: unknown[] | null
  /** Dedicated measurable sell unit (ProportionChoice code) the sell price applies to. */
  sell_unit?: string | number | null
  sell_unit_name?: string | null
  /** Base quantity of sell_unit covered by sell_price (e.g. 1 for per-kg pricing). */
  sell_unit_qty?: string | number | null
  is_active?: boolean
  active?: string | number | boolean | null
  created_at?: string | null
  updated_at?: string | null
}

export interface StoreProductVariantMedia {
  id: string | number
  _id?: string | number | null
  type?: string | number | null
  logo?: string | null
  product_variant?: string | number | null
  product_variant_data?: { id?: string | number; name?: string | null; label?: string | null } | null
}

export interface StoreProductVariantPayload {
  product: string | number
  planner_model?: string | number | null
  label?: string
  name?: string
  description?: string
  logo?: File | string | null
  sku?: string
  price?: string | number | null
  mrp_price?: string | number | null
  sell_price?: string | number | null
  buy_price?: string | number | null
  priority_booking_enabled?: boolean | number | string | null
  priority_booking_default?: boolean | number | string | null
  priority_fee?: string | number | null
  priority_service_time_mins?: string | number | null
  booking_fee?: string | number | null
  taxes_included?: boolean | number | string | null
  tax_label?: string | number | null
  quantity?: string | number
  sell_unit?: number | null
  sell_unit_qty?: string | number | null
  processing_time?: string | number | null
  processing_unit?: 'min' | 'hr' | 'day' | null
  approx_processing_time?: string | number | null
  approx_processing_unit?: 'hr' | 'day' | null
  is_active?: boolean
  active?: string | number | boolean
}

export interface StoreProductVariantPrice {
  id: string | number
  product_variant?: string | number | null
  currency?: string | number | null
  mrp_price?: string | number | null
  sell_price?: string | number | null
  buy_price?: string | number | null
}

export interface StoreProductVariantPricePayload {
  product_variant: string | number
  currency?: string | number
  mrp_price?: string | number
  sell_price?: string | number
  buy_price?: string | number
}

export interface StoreAddonGroup {
  id: string | number
  name: string
  min?: string | number | null
  max?: string | number | null
  active?: number | boolean
  scope_type?: 'global' | 'org' | 'instance' | string | null
  org_instance?: string | number | null
  service_instance?: string | number | null
  service_variant?: string | number | null
  product?: string | number | null
  product_variant?: string | number | null
  product_data?: { id?: string | number; name?: string | null } | null
  product_variant_data?: { id?: string | number; label?: string | null } | null
}

export interface StoreAddonGroupPayload {
  name: string
  min?: string | number
  max?: string | number
  active?: number
  scope_type?: 'global' | 'org' | 'instance' | string
  org_instance?: string | number | null
  service_instance?: string | number | null
  product?: string | number
  product_variant?: string | number
}

export interface StoreVariantAddonGroupLink {
  id: string | number
  product_variant?: string | number | null
  addon_group?: string | number | null
  active?: number | boolean
  addon_group_data?: { id?: string | number; name?: string | null; min?: string | number | null; max?: string | number | null } | null
  product_variant_data?: { id?: string | number; label?: string | null } | null
}

export interface StoreVariantAddonGroupLinkPayload {
  product_variant: string | number
  addon_group: string | number
}

export interface StoreVariantAddonItemLink {
  id: string | number
  product_variant?: string | number | null
  _product_variant_id?: string | number | null
  addon_item?: string | number | null
  _addon_item_id?: string | number | null
  price?: string | number | null
  effective_price?: string | number | null
  catalog_price?: string | number | null
  active?: number | boolean
  addon_item_data?: { id?: string | number; name?: string | null; price?: string | number | null } | null
  addon_group_data?: { id?: string | number; name?: string | null; min?: string | number | null; max?: string | number | null } | null
  product_variant_data?: { id?: string | number; label?: string | null } | null
}

export interface StoreVariantAddonItemLinkPayload {
  product_variant: string | number
  addon_item: string | number
  price?: string | number
}

export interface StoreAddonItem {
  id: string | number
  _id?: string | number | null
  addon_group?: string | number | null
  name?: string | null
  logo?: string | null
  price?: string | number | null
  active?: number | boolean
  default?: number | boolean
  product?: string | number | null
  product_variant?: string | number | null
  planner_resource_links?: PlannerResourceLink[]
  addon_group_data?: { id?: string | number; name?: string | null; min?: string | number | null; max?: string | number | null } | null
  product_data?: { id?: string | number; name?: string | null } | null
  product_variant_data?: { id?: string | number; product_variant_id?: string | number | null; product_variant_name?: string | null; label?: string | null } | null
}

export interface StoreAddonItemPayload {
  addon_group: string | number
  product_variant?: string | number
  product?: string | number
  name?: string
  price?: string | number
  active?: number
}

export interface StoreOffer {
  id: string | number
  name?: string | null
  title?: string | null
  slug?: string | null
  description?: string | null
  scheme?: string | number | null
  scheme_title?: string | null
  offer_scheme_data?: {
    id?: string | number | null
    name?: string | null
    title?: string | null
    effective_from?: string | null
    effective_to?: string | null
  } | null
  offer_type?: string | number | null
  offer_type_label?: string | null
  type?: string | number | null
  status?: string | number | null
  product?: string | number | null
  product_name?: string | null
  product_variant?: string | number | null
  product_variant_name?: string | null
  discount_value?: string | number | null
  discount_type?: string | null
  start_date?: string | null
  end_date?: string | null
  effective_from?: string | null
  effective_to?: string | null
  active?: number | boolean
  draft?: boolean
  max_claim_limit?: number | string | null
  offer_qty_condition_data?: Array<{
    id?: string | number | null
    min?: number | string | null
    max?: number | string | null
    benefits?: Array<{
      id?: string | number | null
      value?: string | number | null
      m?: number | string | null
      n?: number | string | null
    }>
  }>
  /** Django `OfferPlatformType` ids attached to this offer (e.g. 1=In Store, 2=Web, 3=Mobile). */
  offer_platform_data?: Array<number | string> | null
  is_active?: boolean
  created_at?: string | null
  updated_at?: string | null
}

export interface StoreOfferScheme {
  id: string | number
  title: string
  slug?: string | null
  description?: string | null
  effective_from?: string | null
  effective_to?: string | null
  active?: number | boolean
  deleted?: number | boolean
  is_system?: number | boolean
  service_instance_id?: string | number | null
}

export interface StoreOfferPayload {
  title: string
  slug?: string
  scheme: string | number
  type: string | number
  draft: boolean
  active: number | boolean
  max_claim_limit: number | string
  effective_from: string
  effective_to: string
  logo?: string
  thumbnail?: string
  banner?: string
  category: Array<Record<string, unknown>>
  product: Array<Record<string, unknown>>
  qty_condition: Array<Record<string, unknown>>
  beneficiary: Array<Record<string, unknown>>
  platform: Array<number | string>
  total_condition: Array<Record<string, unknown>>
  tag: Array<string | number>
  voucher: Array<Record<string, unknown>>
}

export interface StoreOfferProduct {
  id: string
  _id?: string | number
  offer: string
  offer_slug?: string
  _offer?: string | number
  product_id: string
  _product_id?: string | number
  product_variant_id?: string | null
  _product_variant_id?: string | number | null
  product_name?: string
  product_slug?: string
  short_code?: string
  offer_data?: {
    id?: string | number
    title?: string | null
    offer_logo?: string | null
    active?: number | boolean | null
    deleted?: number | boolean | null
    effective_from?: string | null
    effective_to?: string | null
    scheme_title?: string | null
    scheme_effective_from?: string | null
    scheme_effective_to?: string | null
    discount_value?: string | number | null
    discount_type?: string | null
  } | null
  product_data?: {
    id: string
    _id?: string | number
    name?: string
    logo?: string | null
    description?: string | null
  } | null
  product_variant_data?: {
    id: string
    _id?: string | number
    name?: string | null
    label?: string | null
    code?: string | null
  } | null
  qty_min?: number
  qty_max?: number
  inclusive?: boolean
  ui_visibility?: boolean
  active?: number
}

export interface StoreOfferProductPayload {
  offer: string
  product_variant: string
  product?: string
  qty_min?: number
  qty_max?: number
  inclusive?: boolean
  ui_visibility?: boolean
}

export interface StoreOfferSchemePayload {
  title: string
  slug?: string
  description: string
  effective_from: string
  effective_to: string
  active?: number | boolean
  is_system?: number | boolean
}

export interface StorePosOrderPayload {
  checkout?:boolean
  order_type?: string | number | null
  order_type_id?: string | number | null
  serve_type_id?: string | number | null
  pay_later?: string | number | null
  payment_mode?: string | number | null
  payment_type?: string | number | null
  customer_name?: string
  customer_phone?: string
  customer_group?: 'retail_customer' | 'business_customer'
  customer_address?: string
  customer_user_id?: string | number | null
  user_id?: string | number | null
  note?: string
  payment_method?: string
  order?: string | number | null
  source_type?: string | number | null
  schedule?: boolean
  schedule_time?: string
  applied_offers?: Record<string, unknown> | null
  sub_total?: string | number | null
  savings?: string | number | null
  total?: string | number | null
  uptd_by?: string | number | null
  door_no?: string | number | null
  street_line?: string | number | null
  zipcode?: string | number | null
  country?: string | number | null
  email?: string | number | null
  building_name?: string | number | null
  items: Array<{
    product_variant: string | number
    quantity: number
    basket_item?: string | number | null
    addons?: Array<{
      addon: string | number
      quantity: number
    }>
  }>
}

export interface JobLocation {
  id: string | number
  service_instance?: string | number | null
  client_user: string | number | null
  client_user_info?: {
    id?: string | number
    name?: string
    email?: string | null
    phone?: string | null
  } | null
  place: string
  latitude?: number | null
  longitude?: number | null
  coordinates?: Array<[number, number]> | null
  center_latlog?: string | null
  attendance_device?: string | number | null
  attendance_device_name?: string | null
  attendance_device_sn?: string | null
  active?: boolean
  deleted?: boolean
  linked_job_count?: number
  assigned_worker_count?: number
  online_worker_count?: number
  created_at: string
  updated_at: string
}

export interface JobLocationPayload {
  client_user?: string | number | null
  place: string
  latitude?: number | null
  longitude?: number | null
  coordinates?: Array<[number, number]> | string | null
  center_latlog?: string | null
  attendance_device?: string | number | null
  active?: boolean
  deleted?: boolean
}

// Job types
export type JobRecurrenceRule =
  | 'none'
  | 'daily'
  | 'weekly'
  | 'bi_weekly'
  | 'monthly'
  | 'every_n_days'
  | 'weekly_days'
  | 'monthly_day'

export type JobAcceptanceMode = 'manual' | 'auto'

export interface EventTag {
  id: string | number
  code: string
  title: string
  active?: boolean
  deleted?: boolean
}

export interface Job {
  id: string
  order?: string | number | null
  order_reference_id?: string | null
  order_info?: JobOrderInfo | null
  order_item?: string | number | null
  order_item_info?: {
    id?: string | number | null
    qty?: number | null
    product_id?: string | number | null
    product_name?: string | null
    product_variant_id?: string | number | null
    product_variant_name?: string | null
    final_price?: string | number | null
  } | null
  order_item_features_snapshot?: {
    included_features?: Array<{
      id?: string | number | null
      name?: string | null
      code?: string | null
      feature_type?: string | null
    }>
    excluded_features?: Array<{
      id?: string | number | null
      name?: string | null
      code?: string | null
      feature_type?: string | null
    }>
  } | null
  order_item_features_snapshot_json?: {
    included_features?: Array<{
      id?: string | number | null
      name?: string | null
      code?: string | null
      feature_type?: string | null
    }>
    excluded_features?: Array<{
      id?: string | number | null
      name?: string | null
      code?: string | null
      feature_type?: string | null
    }>
  } | null
  job_ref?: string
  job_code?: string | null
  job_landmark?: string | null
  parent_job?: string | number | null
  parent_event_info?: {
    id?: string | number
    job_ref?: string | null
    job_code?: string | null
    title?: string
    status?: string
    kind_code?: string | null
    kind_label?: string | null
  } | null
  project_info?: {
    id?: string | number
    job_ref?: string | null
    job_code?: string | null
    title?: string
    status?: string
    kind_code?: string | null
    kind_label?: string | null
  } | null
  sub_events_count?: number
  kind_code?: string | null
  title: string
  short_description?: string
  thumbnail?: string | null
  banner?: string | null
  description?: string
  recurrence_parent_info?: {
    id?: string | number
    title?: string
    status?: string
    scheduled_start?: string | null
  } | null
  next_occurrence_info?: {
    id?: string | number
    title?: string
    status?: string
    scheduled_start?: string | null
  } | null
  recurrence_rule?: JobRecurrenceRule
  recurrence_interval_days?: number | null
  recurrence_weekdays?: number[]
  recurrence_month_day?: number | null
  recurrence_count?: number | null
  recurrence_end?: string | null
  recurrence_parent?: string | number | null
  category?: string | number
  category_name?: string
  tags?: EventTag[]
  primary_worker?: string | number | null
  team_template?: string | number | null
  team_template_info?: {
    id?: string | number
    name?: string | null
    code?: string | null
    logo_url?: string | null
  } | null
  staffed_team?: string | number | null
  staffed_team_info?: {
    id?: string | number
    name?: string | null
    code?: string | null
    crew_template?: string | number | null
    is_ready?: boolean | null
    member_count?: number | null
    required_headcount?: number | null
  } | null
  primary_worker_info?: {
    id?: string | number
    name?: string
    mobile?: string
  } | null
  client?: string
  client_user?: string | number | null
  client_user_info?: {
    id?: string | number
    name?: string
    email?: string
    phone?: string
  } | null
  client_name?: string
  client_contact?: string
  email?: string
  phone?: string
  location?: string
  city?: string
  location_id?: string | number | null
  location_info?: JobLocation | null
  latitude?: number | string | null
  longitude?: number | string | null
  address?: string
  status: 'draft' | 'open' | 'in_progress' | 'completed' | 'cancelled' | 'on_hold'
  /** Optional listing visibility. Null/omit = unset (not public). */
  visibility?: 'public' | 'private' | null
  workflow_status?: string | number | null
  workflow_status_code?: string | null
  workflow_status_label?: string | null
  priority: 'low' | 'medium' | 'high' | 'urgent' | 'critical'
  scheduled_start?: string
  scheduled_end?: string
  scheduled_hours?: number
  start_date?: string
  start_time?: string
  end_date?: string
  end_time?: string
  processing_time?: string
  processing_unit?: 'min' | 'hr' | 'day'
  estimated_hours?: number
  actual_hours?: number
  job_pay?: number
  advance_payment?: number
  total_advance_payment?: number
  invoice_amount_due?: number
  job_expenses?: number
  budget?: number
  actual_cost?: number
  total_estimated_spent?: number
  total_actual_spent?: number
  job_summary?: JobSummary
  workday_entry_clash_count?: number
  worker_pay_total?: number
  worker_budget_amount?: number
  payment_due?: number
  is_over_budget?: boolean
  assignment_acceptance_mode?: JobAcceptanceMode
  workday_entry_acceptance_mode?: JobAcceptanceMode
  workday_entry_approval_mode?: JobAcceptanceMode
  is_locked: boolean
  has_invoice?: boolean
  created_at: string
  updated_at: string
  created_by?: string
  branch?: string
}

export interface JobMediaItem {
  id: string | number
  job: string | number
  caption?: string
  file?: string | null
  file_url?: string | null
  file_name?: string | null
  file_type?: 'image' | 'video' | 'audio' | 'other'
  is_before?: boolean
  is_after?: boolean
  created_by?: string | number | null
  uploaded_by_name?: string | null
  created_at?: string
  updated_at?: string
}

export interface JobManagementConfiguration {
  id?: string | number | null
  track_worker: boolean
  push_notification: boolean
  sms_notification: boolean
  email_notification: boolean
  number_gallery_files: number
  maximum_file_size: number
  work_hours_per_day: number
  per_day_worker_invoice_cutoff_time?: string | null
  allow_worker_comments: boolean
  require_otp_for_completion: boolean
  auto_assign_primary_worker: boolean
  allow_expense_uploads: boolean
  allow_multiple_assignments: boolean
  enable_recurrent_job_booking: boolean
  enable_job_audit: boolean
  organisation?: string | number | null
  service_instance?: string | number | null
  client?: string | null
  sender_email?: string | null
  sms_gateway?: string | null
  media_base_url?: string | null
  gallery_storage_backend?: 'local' | 's3'
  gallery_s3_bucket?: string | null
  gallery_s3_region?: string | null
  gallery_s3_access_key?: string | null
  gallery_s3_secret_key?: string | null
  has_gallery_s3_secret_key?: boolean
  created_at?: string
  updated_at?: string
}

export interface JobManagementConfigurationUpdatePayload {
  track_worker?: boolean
  push_notification?: boolean
  sms_notification?: boolean
  email_notification?: boolean
  number_gallery_files?: number
  maximum_file_size?: number
  work_hours_per_day?: number
  per_day_worker_invoice_cutoff_time?: string | null
  allow_worker_comments?: boolean
  require_otp_for_completion?: boolean
  auto_assign_primary_worker?: boolean
  allow_expense_uploads?: boolean
  allow_multiple_assignments?: boolean
  enable_recurrent_job_booking?: boolean
  enable_job_audit?: boolean
  sender_email?: string | null
  sms_gateway?: string | null
  media_base_url?: string | null
  gallery_storage_backend?: 'local' | 's3'
  gallery_s3_bucket?: string | null
  gallery_s3_region?: string | null
  gallery_s3_access_key?: string | null
  gallery_s3_secret_key?: string | null
  clear_gallery_s3_secret_key?: boolean
}

export interface JobCreatePayload {
  title: string
  service_instance_id?: string | number | null
  short_description?: string | null
  thumbnail?: string | null
  banner?: string | null
  description?: string
  order_reference_id?: string | null
  recurrence_rule?: JobRecurrenceRule
  recurrence_interval_days?: number | null
  recurrence_weekdays?: number[]
  recurrence_month_day?: number | null
  recurrence_count?: number | null
  recurrence_end?: string | null
  category: string | number
  primary_worker?: string | number | null
  team_template?: string | number | null
  staffed_team?: string | number | null
  client?: string
  client_user?: string | number | null
  client_name?: string
  client_contact?: string
  email?: string
  phone?: string
  location?: string
  city?: string
  location_id?: string | number | null
  latitude?: number | string | null
  longitude?: number | string | null
  address?: string
  status?: string
  visibility?: 'public' | 'private' | null
  priority?: string
  scheduled_start?: string
  scheduled_end?: string
  scheduled_hours?: number
  start_date?: string
  start_time?: string
  end_date?: string
  end_time?: string
  processing_time?: string
  processing_unit?: 'min' | 'hr' | 'day'
  estimated_hours?: number
  job_pay?: number
  advance_payment?: number
  payment_due?: number
  budget?: number
  assignment_acceptance_mode?: JobAcceptanceMode
  workday_entry_acceptance_mode?: JobAcceptanceMode
  workday_entry_approval_mode?: JobAcceptanceMode
  project_id?: string | number | null
  parent_event_id?: string | number | null
  job_code?: string | null
  job_landmark?: string | null
  is_project?: boolean
  kind?: string
  tag_ids?: Array<string | number>
}

export interface JobCategory {
  id: string
  name: string
  slug: string
  kind?: 'job' | 'project' | 'event'
  service_instance?: string | number | null
  logo_url?: string
  active?: boolean
  deleted?: boolean
  created_at: string
  updated_at: string
}

export interface JobCategoryCreatePayload {
  name: string
  slug: string
  kind?: 'job' | 'project' | 'event'
}

export interface JobCategoryUpdatePayload {
  name?: string
  slug?: string
  kind?: 'job' | 'project' | 'event'
}

// Worker types
export interface Worker {
  id: string
  /** Numeric DB id (worker list APIs); use for matching worker-skill rows. */
  worker_id?: string | number
  /** Six digit worker ID used as ADMS device User ID/PIN. */
  worker_code?: string | null
  pay_scales?: Array<{
    id: string | number
    worker_skill_id?: string | number | null
    skill_role_title?: string | null
    pay_type?: 'per_day' | 'per_hour' | 'per_job' | 'per_week' | 'per_month' | null
    pay_type_label?: string | null
    additional_pay_mode?: 'hourly' | 'flat' | null
    additional_pay_max_hours?: number | string | null
    base_pay?: number | string | null
    night_pay?: number | string | null
    holiday_pay?: number | string | null
    peak_pay?: number | string | null
    overtime_pay?: number | string | null
    special_pay?: number | string | null
    is_active?: boolean
    notes?: string | null
  }>
  worker_skill_mappings?: Array<{
    id: string | number
    skill_role_id?: string | number | null
    skill_role_unique_id?: string | null
    skill_role_title?: string | null
    job_category_id?: string | number | null
    job_category_name?: string | null
    is_primary?: boolean
    is_active?: boolean
  }>
  verified_by?: {
    id: string | number
    username?: string | null
    full_name?: string | null
  } | null
  user_id?: string
  user?: string | number | null
  user_registration_number?: string | null
  worker_name?: string | null
  display_name?: string
  categories?: Array<string | number>
  role?: string | number
  role_name?: string | null
  post?: string | number | null
  linked_post_user_name?: string | null
  linked_post_role_name?: string | null
  linked_post_label?: string | null
  first_name?: string
  last_name?: string
  email?: string
  phone?: string
  phone_number?: string
  client?: string
  avatar?: string
  address?: string
  linked_user_address?: {
    id?: string | number
    name?: string | null
    door_no?: string | null
    building_name?: string | null
    floor_line?: string | null
    street_line?: string | null
    city_name?: string | null
    zipcode?: string | null
    latitude?: string | number | null
    longitude?: string | number | null
    mobile_number?: string | null
    email?: string | null
  } | null
  permanent_latitude?: number | string | null
  permanent_longitude?: number | string | null
  permanent_location_text?: string | null
  permanent_location_updated_at?: string | null
  full_name?: string
  avatar_url?: string
  is_approved?: boolean
  is_active?: boolean
  registration_status?: number | string
  registration_status_label?: string | null
  verified?: boolean
  is_online?: boolean
  isOnline?: boolean
  last_seen_at?: string | null
  last_latitude?: number | string | null
  last_longitude?: number | string | null
  last_location_text?: string | null
  last_location_updated_at?: string | null
  last_lat?: number | string | null
  last_lan?: number | string | null
  last_updated_time?: string | null
  worker_type?: number | null
  contractor_user?: string | number | null
  contractor_user_info?: {
    id?: string | number | null
    name?: string | null
    user_group_code?: string | null
  } | null
  per_hour_pay?: number
  day_pay?: number
  night_pay?: number
  holiday_pay?: number
  overtime_pay?: number
  hotline_current_status?: 'waiting' | 'approval' | 'denied' | 'exited' | null
  hotline_joined_at?: string | null
  hotline_exited_at?: string | null
  hotline_additional_pay?: number | string | null
  hotline_is_active?: boolean
  workday_schedule?: WorkerWorkdayScheduleSummary | null
  total_pay_earned?: number | string
  total_paid_to_worker?: number | string
  current_pay_due?: number | string
  balance_to_pay_to_company?: number | string
  status: 'pending' | 'active' | 'inactive' | 'suspended'
  employment_type: 'full_time' | 'part_time' | 'contract' | 'casual'
  hourly_rate?: number
  daily_rate?: number
  skills?: string[]
  certifications?: string[]
  date_joined?: string
  created_at: string
  updated_at: string
}

export interface WorkerWorkdayScheduleSummary {
  kind: 'current' | 'next' | 'blocking'
  entry_id: string | number
  job_id?: string | number | null
  job_title?: string | null
  work_date?: string | null
  status?: string | null
  status_label?: string | null
  planned_start?: string | null
  planned_end?: string | null
  planned_start_local?: string | null
  planned_end_local?: string | null
  check_in_at?: string | null
  check_out_at?: string | null
  check_in_at_local?: string | null
  check_out_at_local?: string | null
}

export interface WorkerDocument {
  id: string
  worker_id: string
  worker_name?: string
  worker_doc_sec_name?: string
  worker_doc_sec_id?: string
  document_type?: string
  document_front?: string | null
  document_back?: string | null
  expired_date?: string
  effective_from?: string
  effective_to?: string
  status?: number | string
  status_label?: string | null
  ident_no?: string | null
  kyc_doc_id?: string | null
  can_delete?: boolean
}

export interface WorkerDocumentSection {
  id: string
  _id?: string | number
  document_name: string
  type?: number | null
  type_label?: string | null
  country_code?: string | null
  country_code_id?: string | null
  expire_on_date?: string | null
  can_delete?: boolean
  reference_count?: number
}

export interface WorkerVehicle {
  id: string
  worker_id: string
  vehicle_type: string
  make?: string
  model?: string
  year?: number
  registration_number: string
  insurance_expiry?: string
  is_active: boolean
}

export interface WorkerAvailability {
  id: string
  worker_id: string
  day_of_week: number
  start_time: string
  end_time: string
  is_available: boolean
}

export interface WorkerAttendance {
  id: string
  worker_id: string
  status: boolean // true = online, false = offline
  timestamp: string
  latitude?: number
  longitude?: number
  checkout_latitude?: number
  checkout_longitude?: number
  check_in: string
  check_in_local?: string | null
  check_out?: string | null
  check_out_local?: string | null
  total_minutes?: number | null
  out_type?: string | null
  source?: 'mobile' | 'portal' | 'adms' | null
  ip?: string | null
  attendance_location_id?: string | number | null
  attendance_location_name?: string | null
  attendance_device_sn?: string | null
  attendance_device_name?: string | null
}

// Assignment types
export interface Assignment {
  id: string
  assignment_ref?: string | null
  job_id: string
  job?: Job | string | number
  job_title?: string
  job_ref?: string
  worker_id: string | null
  worker?: Worker | string | number | null
  worker_skill?: string | number | null
  worker_skill_label?: string | null
  worker_name?: string | null
  role_label?: string | null
  planner_resource?: string | number | null
  planner_resource_label?: string | null
  crew_template_slot?: string | number | null
  crew_template_id?: string | number | null
  requirement_slot?: number | null
  is_active?: boolean
  pay_type?: 'per_day' | 'per_hour' | 'per_job' | 'per_week' | 'per_month' | null
  pay_type_label?: string | null
  additional_pay_mode?: 'hourly' | 'flat' | null
  additional_pay_max_hours?: number | string | null
  pay_type_snapshot?: 'per_day' | 'per_hour' | 'per_job' | 'per_week' | 'per_month' | null
  pay_scale_base_pay?: number | string | null
  pay?: number | string | null
  night_pay?: number | string | null
  holiday_pay?: number | string | null
  peak_pay?: number | string | null
  overtime_pay?: number | string | null
  pay_flat?: number | string
  day_pay_rate_snapshot?: number | string | null
  night_pay_rate_snapshot?: number | string | null
  holiday_pay_rate_snapshot?: number | string | null
  peak_pay_rate_snapshot?: number | string | null
  overtime_pay_rate_snapshot?: number | string | null
  estimated_duration?: number | string | null
  special_pay?: number | string | null
  total_pay?: number | string
  payment_due_to_company?: number | string
  role?: string
  status?: 'pending' | 'accepted' | 'rejected' | 'auto_rejected' | 'withdrawn' | 'completed'
  workflow_status?: string | number | null
  workflow_status_code?: string | null
  workflow_status_label?: string | null
  parent_assignment?: string | number | null
  parent_assignment_ref?: string | null
  follow_up_assignment_id?: string | number | null
  follow_up_assignment_ref?: string | null
  declined_worker?: string | number | null
  declined_worker_name?: string | null
  rejected_at?: string | null
  rejection_reason?: string | null
  start_date?: string
  end_date?: string
  scheduled_start?: string | null
  scheduled_end?: string | null
  notes?: string
  created_at: string
  updated_at: string
}

export interface AssignmentJobView {
  category: string[]
  title: string
  jobId: string
  bookingId?: string | null
  scheduled?: string | null
  location?: string | null
  distanceKm?: number | null
  startsIn?: string | null
  pay: {
    amount: number
    currency: string
    type: string
  }
  assignedTo: {
    name: string
    phone: string
  }
  jobDescription: string
  customer: {
    name: string
    phone: string
    address: string
  }
  includes: string[]
  excludes: string[]
  beforeEvidence: string[]
  afterEvidence: string[]
  note: string
  status: string
  availableStatuses: string[]
  assignmentId?: string | number
  assignmentStatus?: string
  workerId?: string | number | null
  jobStatus?: string
}

export interface JobAuditEntry {
  id: string | number
  job: string | number
  assignment?: string | number | null
  action: string
  value?: string
  user?: string | number | null
  user_name?: string | null
  timestamp?: string
  created_at?: string
  updated_at?: string
}

export type JobAuditActionOption =
  | 'create'
  | 'update'
  | 'lock'
  | 'unlock'
  | 'soft_delete'
  | 'assignment_create'
  | 'assignment_update'
  | 'assignment_delete'
  | 'assignment_auto_slots_created'
  | 'assignment_auto_workers_assigned'
  | 'assignment_pending'
  | 'assignment_accepted'
  | 'assignment_rejected'
  | 'assignment_auto_rejected'
  | 'assignment_withdrawn'
  | 'assignment_completed'
  | 'workday_create'
  | 'workday_update'
  | 'workday_delete'
  | 'workday_entries_sync'
  | 'workday_entry_create'
  | 'workday_entry_update'
  | 'workday_entry_delete'
  | 'workday_entry_status_change'
  | (string & {})

export type JobWorkflowEntityType = 'job' | 'assignment' | 'workday_entry' | 'attendance' | 'leave_request' | 'task' | 'expense' | 'inventory' | (string & {})
export type JobWorkflowActorType = 'role' | 'post' | 'user' | 'worker_skill' | 'assigned_worker' | 'system' | 'any_authenticated' | (string & {})

type JobWorkflowPayload = Record<string, unknown>

interface JobWorkflowBase {
  id: string | number
  code?: string
  label?: string
  name?: string
  description?: string | null
  active?: boolean
  is_system?: boolean
  system_protected?: boolean
  created_at?: string
  updated_at?: string
}

export interface JobKind extends JobWorkflowBase {
  plural_label?: string
  icon?: string | null
  route_key?: string | null
  is_default?: boolean
}

export interface JobKindChildRule extends JobWorkflowBase {
  parent_kind?: string | number | null
  parent_kind_code?: string | null
  parent_kind_label?: string | null
  child_kind?: string | number | null
  child_kind_code?: string | null
  child_kind_label?: string | null
}

export interface JobWorkflowStatusDefinition extends JobWorkflowBase {}
export interface JobStageDefinition extends JobWorkflowBase {}

export interface JobWorkflowStatus extends JobWorkflowBase {
  definition?: string | number | null
  definition_code?: string | null
  definition_label?: string | null
  entity_type: JobWorkflowEntityType
  kind?: string | number | null
  kind_code?: string | null
  kind_label?: string | null
  is_default?: boolean
  is_terminal?: boolean
  is_editable?: boolean
  reason_required?: boolean
  sorting_order?: number
}

export interface JobWorkflowTransition extends JobWorkflowBase {
  entity_type: JobWorkflowEntityType
  kind?: string | number | null
  kind_code?: string | null
  kind_label?: string | null
  from_status?: string | number | null
  from_status_code?: string | null
  from_status_label?: string | null
  to_status: string | number
  to_status_code?: string | null
  to_status_label?: string | null
  to_status_reason_required?: boolean
  requires_note?: boolean
  sorting_order?: number
}

export interface JobWorkflowTransitionActor extends JobWorkflowBase {
  transition: string | number
  transition_label?: string | null
  actor_type: JobWorkflowActorType
  role?: string | number | null
  role_code?: string | null
  role_label?: string | null
  post?: string | number | null
  post_code?: string | null
  post_label?: string | null
  user?: string | number | null
  user_label?: string | null
  worker_skill?: string | number | null
  worker_skill_label?: string | null
}

export type JobCancellationReasonEntityType = 'booking' | 'job' | 'assignment' | 'workday_entry' | (string & {})

export interface JobCancellationReason extends JobWorkflowBase {
  entity_type: JobCancellationReasonEntityType
  service_variant?: string | number | null
  service_variant_label?: string | null
  organisation?: string | number | null
  organisation_label?: string | null
  sorting_order?: number
}

export interface JobStage extends JobWorkflowBase {
  definition?: string | number | null
  definition_code?: string | null
  definition_label?: string | null
  kind?: string | number | null
  kind_code?: string | null
  kind_label?: string | null
  is_default?: boolean
  is_terminal?: boolean
  sorting_order?: number
}

export interface JobStageTransition extends JobWorkflowBase {
  kind?: string | number | null
  kind_code?: string | null
  kind_label?: string | null
  from_stage?: string | number | null
  from_stage_code?: string | null
  from_stage_label?: string | null
  to_stage: string | number
  to_stage_code?: string | null
  to_stage_label?: string | null
  requires_note?: boolean
  sorting_order?: number
}

export interface JobStageTransitionActor extends JobWorkflowBase {
  transition: string | number
  transition_label?: string | null
  actor_type: JobWorkflowActorType
  role?: string | number | null
  role_code?: string | null
  role_label?: string | null
  post?: string | number | null
  post_code?: string | null
  post_label?: string | null
  user?: string | number | null
  user_label?: string | null
  worker_skill?: string | number | null
  worker_skill_label?: string | null
}

export type JobKindPayload = JobWorkflowPayload
export type JobKindChildRulePayload = JobWorkflowPayload
export type JobWorkflowStatusDefinitionPayload = JobWorkflowPayload
export type JobWorkflowStatusPayload = JobWorkflowPayload
export type JobWorkflowTransitionPayload = JobWorkflowPayload
export type JobWorkflowTransitionActorPayload = JobWorkflowPayload
export type JobCancellationReasonPayload = JobWorkflowPayload
export type JobStageDefinitionPayload = JobWorkflowPayload
export type JobStagePayload = JobWorkflowPayload
export type JobStageTransitionPayload = JobWorkflowPayload
export type JobStageTransitionActorPayload = JobWorkflowPayload

export interface ApplyWorkflowStatusPayload {
  entity_type: JobWorkflowEntityType
  entity_id: string | number
  to_status: string | number
  note?: string
  metadata?: Record<string, unknown>
}

export interface JobWorkflowStatusChange {
  id: string | number
  entity_type: JobWorkflowEntityType
  job?: string | number | null
  assignment?: string | number | null
  work_day_entry?: string | number | null
  attendance?: string | number | null
  leave_request?: string | number | null
  task?: string | number | null
  expense?: string | number | null
  inventory?: string | number | null
  from_status?: string | number | null
  from_status_code?: string | null
  to_status: string | number
  to_status_code?: string | null
  changed_by?: string | number | null
  changed_by_name?: string | null
  changed_as_post?: string | number | null
  changed_as_post_role?: string | null
  note?: string
  metadata?: Record<string, unknown>
  created_at?: string
}

// WorkDay types
export interface WorkDay {
  id: string | number
  job: string | number
  work_date: string
  is_planned?: boolean
  is_worked?: boolean
  notes?: string
  worker_entries_count?: number
  resources_count?: number
  created_at?: string
  updated_at?: string
}

export interface WorkDayEntry {
  id: string | number
  workday_entry_ref?: string | null
  work_day: string | number
  assignment?: string | number | null
  assignment_id?: string | number | null
  assignment_ref?: string | null
  worker: string | number
  worker_skill?: string | number | null
  worker_skill_label?: string | null
  job: string | number
  workday?: WorkDay
  workerProfile?: Worker
  work_date?: string
  worker_name?: string
  job_title?: string
  planned_start?: string | null
  planned_start_local?: string | null
  planned_end?: string | null
  planned_end_local?: string | null
  pay_type?: string | null
  pay_hourly_rate?: number | string | null
  pay_flat?: number | string | null
  additional_pay_mode?: 'hourly' | 'flat' | null
  additional_pay_max_hours?: number | string | null
  night_pay?: number | string | null
  holiday_pay?: number | string | null
  peak_pay?: number | string | null
  overtime_pay?: number | string | null
  special_pay?: number | string | null
  peak_time_approved?: boolean
  holiday_approved?: boolean
  special_day_approved?: boolean
  over_time_approved?: boolean
  night_pay_approved?: boolean
  payable_by_client?: boolean
  client_pay_amount?: number | string | null
  total_pay?: number | string
  check_in?: string | null
  check_in_local?: string | null
  check_out?: string | null
  check_out_local?: string | null
  org_timezone?: string | null
  org_time_zone?: string | null
  hours?: number | string
  planned_duration_minutes?: number
  actual_duration_minutes?: number
  is_late?: boolean
  late_minutes?: number
  has_overtime?: boolean
  overtime_minutes?: number
  needs_supervisor_attention?: boolean
  rating?: number | string | null
  timely_arrived_rating?: number | string | null
  quality_work_rating?: number | string | null
  conduct_rating?: number | string | null
  review_comment?: string | null
  reviewed_by?: string | number | null
  reviewed_by_name?: string | null
  reviewed_at?: string | null
  status?: 'draft' | 'pending_acceptance' | 'scheduled' | 'schedule_declined' | 'in_progress' | 'completed' | 'accepted' | 'rejected' | 'logged'
  worker_schedule_status?: 'pending' | 'accepted' | 'rejected'
  status_label?: string
  status_is_final?: boolean
  status_hint?: string
  overlap_conflict_count?: number
  overlap_conflicts?: WorkDayEntryOverlapConflict[]
  notes?: string
  created_at: string
  updated_at: string
}

export interface WorkDayEntryOverlapConflict {
  id: string | number
  job: string | number
  job_title?: string | null
  work_day?: string | number | null
  work_date?: string | null
  worker?: string | number | null
  worker_name?: string | null
  start?: string | null
  end?: string | null
}

export type JobChecklistItemStatus = 'pending' | 'completed' | 'skipped' | 'reopened'

export interface JobChecklistItem {
  id: string | number
  job: string | number
  work_day?: string | number | null
  title: string
  description?: string
  sort_order?: number
  is_required?: boolean
  requires_photo?: boolean
  requires_signature?: boolean
  status: JobChecklistItemStatus
  completed_at?: string | null
  completed_by?: string | number | null
  completed_by_name?: string | null
  has_signature?: boolean
  signature_uploaded_at?: string | null
  signature_customer_name?: string | null
  notes?: string
  created_at?: string
  updated_at?: string
}

export interface JobChecklistSummary {
  total: number
  completed: number
  required_total: number
  required_completed: number
  all_required_complete: boolean
}

export interface JobChecklistPayload {
  items: JobChecklistItem[]
  summary: JobChecklistSummary
  template_available?: boolean
}

export interface JobChecklistTemplateItem {
  id?: string | number
  title: string
  description?: string
  sort_order?: number
  scope?: 'job' | 'work_day'
  is_required?: boolean
  requires_photo?: boolean
  requires_signature?: boolean
}

export interface JobChecklistTemplate {
  id: string | number
  name: string
  description?: string
  kind?: 'job' | 'project'
  job_category?: string | number | null
  job_category_name?: string | null
  is_default?: boolean
  active?: boolean
  items?: JobChecklistTemplateItem[]
  created_at?: string
  updated_at?: string
}

// Finance types
export interface Expense {
  id: string
  job_id?: string
  job?: Job | string | number
  job_title?: string
  title?: string
  notes?: string
  description?: string
  worker?: string | number | null
  worker_name?: string | null
  category?: string | number | null
  category_name?: string | null
  supplier?: string | number | null
  supplier_name?: string | null
  supplier_user_group_title?: string | null
  amount: number | string
  date?: string
  incurred_at?: string
  status?: 'draft' | 'submitted' | 'approved' | 'rejected' | 'void'
  status_label?: string
  payment_status?: 'unpaid' | 'partial' | 'paid'
  total_paid_amount?: number | string
  outstanding_amount?: number | string
  payment_count?: number
  receipt_url?: string
  billable?: boolean
  reimbursable?: boolean
  paid_by_company?: boolean
  is_billable?: boolean
  is_approved?: boolean
  approved_by?: string
  created_at: string
  updated_at: string
}

export interface ExpenseCategory {
  id: string | number
  service_instance?: string | number | null
  name: string
  slug?: string
  description?: string
  is_active?: boolean
  category_key?: 'exp' | 'inv'
  can_delete?: boolean
  linked_expense_count?: number
  linked_particular_count?: number
  created_at?: string
  updated_at?: string
}

export type BookingQuotationStatus = 'initiated' | 'draft' | 'sent' | 'accepted' | 'converted' | 'cancelled' | 'expired'

export type BookingQuotationOrderItemType = 'service' | 'product' | 'package'

export interface BookingQuotationOrderItemAddon {
  id?: string | number
  addon?: string | number | null
  addon_name?: string | null
  product_variant?: string | number | null
  qty?: string | number
  price?: string | number
}

export interface BookingQuotationOrderItem {
  id?: string | number
  product?: string | number | null
  product_variant?: string | number | null
  product_name?: string | null
  product_variant_label?: string | null
  item_type?: BookingQuotationOrderItemType
  qty: string | number
  unit_price: string | number
  final_price?: string | number | null
  notes?: string | null
  priority_booking_selected?: boolean
  priority_fee?: string | number
  booking_fee?: string | number
  location_text?: string
  addons?: BookingQuotationOrderItemAddon[]
}

export interface BookingQuotationInventoryLine {
  id?: string | number
  quotation?: string | number
  title: string
  description?: string
  inventory_category_id?: string | number | null
  supplier?: string | number | null
  quantity: string | number
  unit_price: string | number
  line_total?: string | number
  tax_label?: string | number | null
  taxes_included?: boolean
  tax_amount?: string | number
  payable_by_client?: boolean
  to_be_purchased?: boolean
  purchase_by?: string | null
  sort_order?: number
  metadata?: Record<string, unknown>
}

export interface BookingQuotation {
  id: string | number
  quotation_number: string
  service_instance?: string | number
  org_instance?: string | number | null
  customer?: string | number | null
  customer_mode?: 'new' | 'existing'
  customer_segment?: 'retail_customer' | 'business_customer'
  customer_name?: string | null
  customer_email?: string | null
  customer_phone?: string | null
  billing_address?: string
  service_address?: string
  scheduled_date?: string | null
  scheduled_start_time?: string | null
  scheduled_end_time?: string | null
  notes?: string
  terms?: string
  enquiry?: string | number | null
  enquiry_name?: string | null
  source_followup?: string | number | null
  source_followup_title?: string | null
  source_followup_status?: string | null
  contract_document_url?: string | null
  contract_document_name?: string | null
  contract_document_file_url?: string | null
  effective_contract_document_url?: string | null
  quotation_details?: Record<string, unknown> | unknown[]
  status: BookingQuotationStatus
  valid_until?: string | null
  subtotal?: string | number
  tax_amount?: string | number
  total?: string | number
  total_snapshot?: {
    subtotal?: string | number
    tax_amount?: string | number
    included_tax_amount?: string | number
    total?: string | number
    tax_rows?: Array<{ label?: string; rate?: string | number; amount?: string | number; included?: boolean }>
    total_rows?: Array<{ label?: string; amount?: string | number; included?: boolean }>
  }
  accepted_snapshot?: Record<string, unknown>
  accepted_at?: string | null
  converted_at?: string | null
  order?: string | number | null
  order_id?: string | number | null
  order_reference?: string | number | null
  order_items_data?: BookingQuotationOrderItem[]
  inventory_lines?: BookingQuotationInventoryLine[]
  created_on?: string
  updated_on?: string
}

export interface BookingQuotationPayload {
  customer?: string | number | null
  customer_mode?: 'new' | 'existing'
  customer_segment?: 'retail_customer' | 'business_customer'
  customer_name?: string | null
  customer_email?: string | null
  customer_phone?: string | null
  billing_address?: string
  service_address?: string
  scheduled_date?: string | null
  scheduled_start_time?: string | null
  scheduled_end_time?: string | null
  notes?: string
  terms?: string
  enquiry?: string | number | null
  source_followup?: string | number | null
  contract_document_url?: string | null
  contract_document_name?: string | null
  quotation_details?: Record<string, unknown> | unknown[]
  status?: BookingQuotationStatus
  valid_until?: string | null
  order_items?: BookingQuotationOrderItem[]
  inventory_lines?: BookingQuotationInventoryLine[]
}

export interface ExpensePayment {
  id: string | number
  expense: string | number
  expense_title?: string
  amount: number | string
  payment_mode: 'cash' | 'card' | 'bank_transfer' | 'upi' | 'cheque' | 'wallet' | 'other'
  paid_on?: string
  reference_number?: string
  transaction_id?: string
  notes?: string
  created_at?: string
  updated_at?: string
}

export interface Particular {
  id: string
  job_id: string
  job?: Job
  title?: string
  notes?: string
  description?: string
  category?: string | number | null
  category_name?: string | null
  responsible_worker?: string | number | null
  responsible_worker_name?: string | null
  work_day_entry?: string | number | null
  work_day_entry_work_date?: string | null
  work_day_entry_worker_name?: string | null
  to_be_purchased?: boolean
  purchase_by?: string | null
  status?: string | null
  status_label?: string | null
  purchase_status?: string | null
  purchase_status_label?: string | null
  in_stock?: boolean
  billable_quantity?: number | string
  client_line_total?: number | string
  taxes_included?: boolean | number | string | null
  tax_label?: string | number | null
  tax_label_data?: TaxLabelData | null
  supplier?: string | number | null
  supplier_name?: string | null
  expected_arrival_date?: string | null
  purchase_order_ref?: string | null
  ordered_quantity?: number | string
  received_quantity?: number | string
  available_quantity?: number | string
  quantity: number | string
  cost?: number | string
  unit_price: number | string
  total_price?: number | string
  line_total?: number | string
  payable_by_client?: boolean
  is_taxable?: boolean
  created_at: string
  updated_at: string
}

export interface ParticularStatusEvent {
  id: string | number
  particular?: string | number | null
  job: string | number
  event_type: 'created' | 'status_change' | 'in_stock_change' | 'deleted'
  event_type_label?: string
  from_status?: string
  from_status_label?: string
  to_status?: string
  to_status_label?: string
  in_stock?: boolean | null
  ordered_quantity?: number | string | null
  received_quantity?: number | string | null
  available_quantity?: number | string | null
  supplier?: string | number | null
  supplier_name?: string | null
  expected_arrival_date?: string | null
  notes?: string
  actor?: string | number | null
  actor_name?: string | null
  created_at: string
}

export interface ExpenseStatusEvent {
  id: string | number
  expense: string | number
  job: string | number
  event_type: 'created' | 'status_change' | 'updated' | 'deleted'
  event_type_label?: string
  from_status?: string
  from_status_label?: string
  to_status?: string
  to_status_label?: string
  notes?: string
  actor?: string | number | null
  actor_name?: string | null
  created_at: string
}

export interface InvoicePreview {
  job_id: string | number
  title: string
  client?: string
  customer_name?: string
  currency?: string | null
  scheduled_start?: string | null
  scheduled_end?: string | null
  particulars: Particular[]
  expenses: Expense[]
  labour_entries?: Array<{
    id: string | number
    worker?: string | number
    worker_name?: string
    work_date?: string
    hours?: number | string
    client_pay_amount?: number | string
    notes?: string
  }>
  payroll: Array<{
    worker_id?: string | number
    worker_name?: string
    total_hours?: number | string
    total_pay?: number | string
  }>
  total_hours: number | string
  total_pay: number | string
  worker_pay_total?: number | string
  total_expenses: number | string
  total_particulars: number | string
  customer_payable?: number | string
  tax_amount?: number | string
  included_tax_amount?: number | string
  tax_breakdown?: Array<{
    name?: string | null
    tax_label_name?: string | null
    value?: string | number | null
    tax_amount?: string | number | null
    included?: boolean | number | string | null
  }>
  subtotal?: number | string
  tax_rows?: Array<{
    label?: string | null
    amount?: number | string | null
    rate?: string | number | null
    scope?: string | null
  }>
  included_tax_note?: string | null
  total_rows?: Array<{
    label?: string | null
    amount?: number | string | null
    kind?: string | null
  }>
  grand_total: number | string
  advance_payment?: number | string
  advance_payments?: JobInvoicePayment[]
  payment_due?: number | string
  issuer_tax_country_code?: string | null
  issuer_tax_registration_type?: string | null
  issuer_tax_registration_label?: string | null
  issuer_tax_registration_number?: string | null
  due_date?: string | null
  door?: string
  first_line?: string
  second_line?: string
  city?: string
  country?: string
  postcode?: string
  email?: string
  phone?: string
  created_at?: string
  snapshot_frozen?: boolean
  invoice_id?: string | number
  invoice_no?: string
  invoice_status?: string
  paid_status?: string
  snapshot_captured_at?: string
}

export interface JobInvoiceSnapshotDocument {
  version: number
  captured_at?: string
  invoice?: {
    id?: string | number
    invoice_no?: string
    status?: string
    paid_status?: string
    issued_at?: string | null
    due_date?: string | null
    notes?: string
  }
  job?: {
    id?: string | number
    job_ref?: string
    title?: string
    status?: string
    completed_at?: string | null
  }
  issuer?: {
    tax_registration?: {
      issuer_tax_country_code?: string | null
      issuer_tax_registration_type?: string | null
      issuer_tax_registration_label?: string | null
      issuer_tax_registration_number?: string | null
    }
  }
  preview?: InvoicePreview
}

export interface JobInvoice {
  id: string | number
  job: string | number
  job_title?: string
  client_name?: string | null
  invoice_no: string
  invoice_number?: string
  status: 'draft' | 'issued' | 'void'
  paid_status?: 'paid' | 'unpaid'
  issued_at?: string | null
  due_date?: string | null
  snapshot_total_hours: number | string
  snapshot_total_pay: number | string
  snapshot_total_expenses: number | string
  snapshot_total_particulars: number | string
  snapshot_grand_total: number | string
  snapshot_document?: JobInvoiceSnapshotDocument | null
  advance_payment?: number | string
  advance_payments?: JobInvoicePayment[]
  payment_due?: number | string
  amount_paid?: number | string
  outstanding?: number | string
  payment_url?: string | null
  payment_reference?: string | null
  can_pay?: boolean
  notes?: string
  created_at?: string
  updated_at?: string
}

export interface JobInvoicePayment {
  id: string | number
  job?: string | number
  invoice?: string | number | null
  invoice_number?: string | null
  job_id?: string | number | null
  job_title?: string
  amount: number | string
  payment_date: string
  payment_method?: {
    id?: string | number
    wallet_name?: string | null
    label?: string | null
    wallet_variant_name?: string | null
    wallet_type?: number | string | null
    wallet_gateway?: number | string | null
  } | string | number | null
  payment_method_id?: string | number | null
  payment_method_label?: string | null
  payment_stage?: 'advance' | 'invoice_payment'
  payment_source?: string | number | null
  payment_source_label?: string | null
  paid_status?: 'paid' | 'unpaid'
  reference?: string
  notes?: string
  created_at?: string
  updated_at?: string
}

export interface WorkerPayment {
  id: string
  worker_id: string
  worker?: Worker
  period_start: string
  period_end: string
  total_hours: number
  hourly_rate: number
  gross_amount: number
  deductions: number
  net_amount: number
  status: 'pending' | 'processed' | 'paid'
  paid_at?: string
}

// Address types
export interface Address {
  id: string | number
  type: 'home' | 'work' | 'billing' | 'other'
  type_id?: number | null
  label?: string
  street?: string
  street_address?: string
  address_line1?: string
  address_line2?: string
  city?: string
  state?: string
  state_name?: string | null
  state_id?: string | number | null
  postal_code?: string
  country?: string
  country_name?: string | null
  country_id?: string | number | null
  latitude?: string | null
  longitude?: string | null
  status?: number | null
  door_no?: string | null
  building_name?: string | null
  is_primary?: boolean
  is_default?: boolean
  access_instructions?: string | null
  location_pin?: string | null
  entry_code?: string | null
  opening_time?: string | null
  closing_time?: string | null
  saturday_closed?: boolean
  sunday_closed?: boolean
}

export interface UserProfile {
  id?: string | number
  usr_reg_num?: string | null
  first_name?: string
  last_name?: string
  full_name?: string
  email?: string
  phone?: string
  phn_nbr?: string
  avatar_url?: string
  photo_url?: string
  email_verified?: boolean
  phone_verified?: boolean
  gender?: string | number | null
  country_id?: string | number | null
  registration_status?: string | number | null
  addresses?: Address[]
}

// Contact types
export interface ContactInfo {
  id: string
  type: 'email' | 'phone' | 'mobile' | 'fax'
  value: string
  is_primary: boolean
}

// Dashboard types
export interface DashboardStats {
  total_jobs: number
  active_jobs: number
  completed_jobs: number
  total_workers: number
  active_workers: number
  pending_approvals: number
  total_revenue: number
  total_expenses: number
  profit: number
}

export type DashboardPeriodType = 'today' | 'tomorrow' | 'this_week' | 'this_month' | 'last_month' | 'custom'

export interface DashboardExpenseWatchItem {
  id: string
  source_type: 'expense' | 'purchase'
  job_id?: number | string | null
  job_title?: string | null
  title: string
  amount: number
  due_date?: string | null
  status: string
  secondary_label?: string | null
}

export interface DashboardWorkerPaymentWatchItem {
  id: number | string
  worker_id: number | string
  worker_name: string
  period_start?: string | null
  period_end?: string | null
  amount_due: number
  jobs_count: number
  status: string
}

export interface DashboardPendingInvoiceItem {
  id: number | string
  invoice_number: string
  job_id?: number | string | null
  job_title?: string | null
  client_name?: string | null
  status: 'draft' | 'issued'
  due_date?: string | null
  issued_at?: string | null
  amount_due: number
}

export interface DashboardUnassignedAssignmentItem {
  id: number | string
  job_id?: number | string | null
  job_title?: string | null
  job_ref?: string | null
  role_label?: string | null
  requirement_slot?: number | null
  scheduled_start?: string | null
}

export interface DashboardAttentionWorkDayEntryItem {
  id: number | string
  job_id?: number | string | null
  job_title?: string | null
  worker_name?: string | null
  work_date?: string | null
  status?: string | null
  notes?: string | null
}

export interface DashboardClashedScheduleItem {
  id: number | string
  job_id?: number | string | null
  job_title?: string | null
  worker_name?: string | null
  work_date?: string | null
  status?: string | null
  notes?: string | null
  overlap_conflict_count?: number
  overlap_conflicts?: WorkDayEntryOverlapConflict[]
}

export interface DashboardWorkdayAttendanceAlertItem {
  id: number | string
  alert_type?: 'late' | 'no_show'
  job_id?: number | string | null
  job_title?: string | null
  worker_name?: string | null
  work_date?: string | null
  status?: string | null
  planned_start?: string | null
  planned_start_local?: string | null
  late_minutes?: number
}

export interface DashboardUpcomingWorkdayScheduleItem {
  id: number | string
  job_id?: number | string | null
  job_title?: string | null
  worker_name?: string | null
  work_date?: string | null
  status?: string | null
  planned_start?: string | null
  planned_end?: string | null
  planned_start_local?: string | null
  planned_end_local?: string | null
}

export interface DashboardOverview {
  period: {
    type: DashboardPeriodType
    label: string
    start: string
    end: string
  }
  summary: {
    jobs_count: number
    workers_needed_count: number
    unique_workers_count: number
    in_progress_jobs_count: number
    completed_jobs_count: number
    cancelled_jobs_count: number
    next_7_days_jobs_count: number
    next_7_days_workers_needed_count: number
    next_7_days_workday_schedules_count: number
    unassigned_assignments_count: number
    service_unassigned_assignments_count: number
    next_7_days_unassigned_assignments_count: number
    attention_work_day_entries_count: number
    service_attention_work_day_entries_count: number
    clashed_schedules_count: number
    late_work_day_entries_count: number
    no_show_work_day_entries_count: number
    active_workers_count: number
    online_workers_count: number
  }
  financials: {
    projected_revenue: number
    upcoming_expenses_total: number
    outstanding_expenses_total: number
    pending_worker_payments_total: number
    pending_worker_periods_count: number
    pending_invoices_total: number
    pending_invoices_count: number
    pending_orders_total?: number
  }
  upcoming_expenses: DashboardExpenseWatchItem[]
  upcoming_worker_payments: DashboardWorkerPaymentWatchItem[]
  pending_invoices: DashboardPendingInvoiceItem[]
  unassigned_assignments: DashboardUnassignedAssignmentItem[]
  attention_work_day_entries: DashboardAttentionWorkDayEntryItem[]
  upcoming_workday_schedules: DashboardUpcomingWorkdayScheduleItem[]
  clashed_schedules: DashboardClashedScheduleItem[]
  late_work_day_entries: DashboardWorkdayAttendanceAlertItem[]
  no_show_work_day_entries: DashboardWorkdayAttendanceAlertItem[]
}

export interface LiveOperation {
  id: string
  type: string
  title: string
  description: string
  timestamp: string
  priority: 'low' | 'medium' | 'high'
}
